import { Box, Stack, styled, Typography } from "@mui/material";
import QuickGenerate from "./_components/quick-generate";
import ReportStats from "./_components/report-stats";
import { useSearchParams } from "react-router-dom";
import { ReportColumns } from "./_components/report-column";
import { DataTable } from "@/components/custom/data-table";
import CustomPagination from "@/components/custom/custom-pagination";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { useReports } from "@/hooks/use-reports";
import { useReportOptions } from "@/hooks/use-report-options";
import { getReportTypeMeta, formatOptionLabel } from "../data";

const RootWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const TopWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  "& > *": { flex: 1 },
}));

const InternalReporting = () => {
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const type = searchParams.get("type");
  const status = searchParams.get("status");

  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);

  const queryParams = {
    page: currentPage,
    size: pageSize,
    search: searchValue,
    type: type || undefined,
    status: status || undefined,
  };

  const { data: reports, isLoading, isError } = useReports(true, queryParams);
  const { types, statuses } = useReportOptions();

  const typeOptions = types.map((t) => ({
    label: getReportTypeMeta(t).label,
    value: t,
  }));
  const statusOptions = statuses.map((s) => ({
    label: formatOptionLabel(s),
    value: s,
  }));

  return (
    <RootWrapper>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Internal Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate, review and export detailed financial reports
        </Typography>
      </Box>

      <TopWrapper>
        <Card>
          <QuickGenerate />
        </Card>

        <ReportStats />
      </TopWrapper>

      <Card>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1rem" }}>
            Generated Reports
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <CustomSearchInput paramKey="search" placeholder="Search reports..." />
          <CustomSelectInput
            paramKey="type"
            label="Type"
            placeholder="All Types"
            options={typeOptions}
          />
          <CustomSelectInput
            paramKey="status"
            label="Status"
            placeholder="All Status"
            options={statusOptions}
          />
        </Stack>

        <DataTable
          columns={ReportColumns}
          data={reports?.data ?? []}
          isLoading={isLoading}
          isError={isError}
          noDataMessage="No reports found."
        />
        <CustomPagination
          total={reports?.totalCount ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>
    </RootWrapper>
  );
};

export default InternalReporting;
